import React, { useEffect, useState } from 'react';
import { Users } from '@phosphor-icons/react';

import GenericFilters from '../../../shared/components/ui/GenericFilters';
import GenericTables from '../../../shared/components/ui/GenericTables';
import PageHeader from '../../../shared/components/ui/PageHeader';
import { userService } from '../../../services/users/userService.js';
import { teamAssignmentService } from '../services/teamAssignmentService.js';
import { teamService } from '../../../services/teams/teamService.js';
import { teamVehicleService } from '../services/teamVehicleService.js';

const HistoricsTeams = () => {
  const [teams, setTeams] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const formatDateToLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const buildInitialFilters = () => ({
    startDate: '',
    endDate: formatDateToLocal(new Date()),
    userEmail: '',
    vehicleId: '',
    teamId: ''
  });

  const [filters, setFilters] = useState(buildInitialFilters);

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    fetchReferenceData();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [filters.startDate, filters.endDate, filters.userEmail, filters.vehicleId, filters.teamId, currentPage, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const fetchReferenceData = async () => {
    try {
      setFiltersLoading(true);

      const [teamsResult, techniciansResult, vehiclesResult] = await Promise.allSettled([
        teamService.getTeams(),
        userService.getTechnicians(),
        teamVehicleService.getVehicles()
      ]);

      const nextTeams = teamsResult.status === 'fulfilled' ? (teamsResult.value || []) : [];
      const nextTechnicians = techniciansResult.status === 'fulfilled' ? (techniciansResult.value || []) : [];
      const nextVehiclesRaw = vehiclesResult.status === 'fulfilled' ? (vehiclesResult.value || []) : [];

      setTeams(Array.isArray(nextTeams) ? nextTeams : []);
      setTechnicians(Array.isArray(nextTechnicians) ? nextTechnicians : []);
      setVehicles(Array.isArray(nextVehiclesRaw) ? nextVehiclesRaw : []);

      if (
        teamsResult.status === 'rejected' &&
        techniciansResult.status === 'rejected' &&
        vehiclesResult.status === 'rejected'
      ) {
        setError('No se pudieron cargar los filtros del historial.');
      }
    } catch (fetchError) {
      console.error('Error loading history filters:', fetchError);
      setError('No se pudieron cargar los filtros del historial.');
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      setError(null);

      const response = await teamAssignmentService.getAssignmentHistory({
        startDate: filters.startDate,
        endDate: filters.endDate,
        userEmail: filters.userEmail,
        vehicleId: filters.vehicleId,
        teamId: filters.teamId,
        pageSize,
        pageCurrent: currentPage - 1
      });

      const data = response.history || [];
      setHistoryItems(Array.isArray(data) ? data : []);

      if (response.pagination) {
        const responseTotalItems = Number(response.pagination.totalItems) || 0;
        const responsePageSize = Number(response.pagination.pageSize) || pageSize;

        setTotalItems(responseTotalItems);
        setTotalPages(responseTotalItems > 0 ? Math.ceil(responseTotalItems / responsePageSize) : 1);
      } else {
        setTotalItems(data.length);
        setTotalPages(1);
      }
    } catch (fetchError) {
      console.error('Error loading team history:', fetchError);
      setHistoryItems([]);
      setTotalItems(0);
      setTotalPages(1);
      setError('No se pudo cargar el historial de equipos.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setHistoryLoading(true);
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setHistoryLoading(true);
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setHistoryLoading(true);
    setFilters(buildInitialFilters());
    setCurrentPage(1);
  };

  const getVehicleName = (row) => {
    if (!row) return 'N/A';

    if (row.vehicleName) {
      return row.vehicleName;
    }

    if (!row.vehicleId) return 'Sin vehiculo';
    const vehicle = vehicles.find((item) => item.id === row.vehicleId || item.thingId === row.vehicleId);
    return vehicle?.name || `Vehiculo ${row.vehicleId}`;
  };

  const filterColumns = [
    {
      type: 'date',
      key: 'startDate',
      label: 'Fecha Inicio Desde',
      colSpan: 1
    },
    {
      type: 'date',
      key: 'endDate',
      label: 'Fecha Fin Hasta',
      colSpan: 1
    },
    {
      type: 'select',
      key: 'userEmail',
      label: 'Tecnico',
      options: [{ email: '', name: 'Todos' }, ...technicians],
      optionValue: 'email',
      optionLabel: 'name',
      colSpan: 1
    },
    {
      type: 'select',
      key: 'vehicleId',
      label: 'Vehiculo',
      options: [{ id: '', name: 'Todos' }, ...vehicles],
      optionValue: 'id',
      optionLabel: 'name',
      colSpan: 1
    },
    {
      type: 'select',
      key: 'teamId',
      label: 'Equipo',
      options: [{ id: '', name: 'Todos' }, ...teams],
      optionValue: 'id',
      optionLabel: 'name',
      colSpan: 1
    }
  ];

  const tableColumns = [
    {
      key: 'team',
      label: 'Equipo',
      align: 'center',
      render: (row) => row.team?.name || 'N/A'
    },
    {
      key: 'technicians',
      label: 'Tecnicos',
      align: 'center',
      render: (row) => {
        if (!row.technicians || row.technicians.length === 0) return 'N/A';
        return (
          <div className="text-left">
            {row.technicians.map((technician, index) => (
              <div key={technician.id || technician.email || index}>
                {index + 1}. {technician.name || 'N/A'}
              </div>
            ))}
          </div>
        );
      }
    },
    {
      key: 'vehicle',
      label: 'Vehiculo',
      align: 'center',
      render: (row) => getVehicleName(row)
    },
    {
      key: 'joinDate',
      label: 'Fecha Inicio',
      align: 'center',
      render: (row) => formatDateForDisplay(row.joinDate)
    },
    {
      key: 'leaveDate',
      label: 'Fecha Fin',
      align: 'center',
      render: (row) => formatDateForDisplay(row.leaveDate)
    }
  ];
  return (
    <div className="space-y-6">
      <PageHeader
        title="Historial de Equipos"
        subtitle="Ver historial de equipos"
        icon={<Users size={28} />}
      />

      {error && (
        <div className="rounded-lg border border-error bg-error/12 p-3 text-sm text-error">
          {error}
        </div>
      )}

      <GenericFilters
        columns={filterColumns}
        filters={filters}
        onFilterChange={(nextFilters) => {
          setHistoryLoading(true);
          setFilters(nextFilters);
          setCurrentPage(1);
        }}
        onClearFilters={clearFilters}
        defaultOpen={false}
        loading={filtersLoading}
      />

      <GenericTables
        columns={tableColumns}
        data={historyItems}
        emptyMessage="No hay asignaciones pasadas"
        zebraStripe
        showFooter
        loading={historyLoading}
        loadingMessage="Cargando historial..."
        tableContainerClassName={historyLoading && historyItems.length === 0 ? 'min-h-[320px]' : ''}
        pagination={{
          currentPage,
          totalPages,
          pageSize,
          totalItems,
          pageSizeOptions: [5, 10, 20, 50],
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }}
      />
    </div>
  );
};

export default HistoricsTeams;
